import { useState, useEffect } from 'react';
import styles from './appointmentEditor.module.css'; // or './AppointmentEditor.css'
import { Icons } from '../../../../code/IconHandler';
import Select from 'react-select';
export default function AppointmentEditor({ appointments, onChange }) {
    const [localAppointments, setLocalAppointments] = useState(appointments || []);
    const [locations, setLocations] = useState([]);

    const handleAppointmentChange = (index, field, value) => {
        const updated = [...localAppointments];
        updated[index][field] = value;
        // Normalize location_id: convert '' to null
        if (field === 'location_id' && (value === '' || value === undefined)) {
            updated[index].location_id = null;
        }
        updated[index].recurrence_rule = buildRecurrenceRule(updated[index]);
        setLocalAppointments(updated);
        onChange(updated);
    };
    useEffect(() => {
        if (!appointments) return;

        const parsedAppointments = appointments.map(app => {
            const { recurrence_freq, recurrence_byday, recurrence_until } = parseRecurrenceRule(app.recurrence_rule);

            return {
                ...app,
                recurrence_freq,
                recurrence_byday,
                recurrence_until,
            };
        });

        setLocalAppointments(parsedAppointments);
    }, [appointments]);
    useEffect(() => {
        async function fetchLocations() {
            try {
                const res = await fetch('/api/sportstaetten/');
                const data = await res.json();

                const sorted = data
                    .map(loc => {
                        const label = loc.bezeichnung
                            ? `${loc.bezeichnung}: ${loc.name}`
                            : loc.name;

                        return {
                            value: loc.id, // or loc.id if more reliable
                            label,
                            ...loc
                        };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label));

                setLocations(sorted);
            } catch (err) {
                console.error('Fehler beim Laden der Orte:', err);
            }
        }

        fetchLocations();
    }, []);
    const handleAdd = () => {
        const newAppointment = {
            start_time: '',
            end_time: '',
            appointment_date: '',
            recurrence_rule: '',
            recurrence_freq: '',
            recurrence_until: '',
            recurrence_byday: [],
            location_id: '',
            additional_text: '',
            exceptions: [],
            topic_type: 'sport_module',
        };
        const updated = [...localAppointments, newAppointment];
        setLocalAppointments(updated);
        onChange(updated);
    };

    const buildRecurrenceRule = ({ recurrence_freq, recurrence_byday, recurrence_until }) => {
        if (!recurrence_freq) return '';

        const parts = [`FREQ=${recurrence_freq}`];

        if (recurrence_byday && recurrence_byday.length > 0) {
            const dayMap = { Mo: 'MO', Di: 'TU', Mi: 'WE', Do: 'TH', Fr: 'FR', Sa: 'SA', So: 'SU' };
            const byDay = recurrence_byday.map(d => dayMap[d] || d).join(',');
            parts.push(`BYDAY=${byDay}`);
        }

        if (recurrence_until) {
            const until = new Date(recurrence_until).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            parts.push(`UNTIL=${until}`);
        }

        return parts.join(';');
    };

    const parseRecurrenceRule = (rule) => {
        if (!rule) return {
            recurrence_freq: '',
            recurrence_byday: [],
            recurrence_until: '',
        };

        const parts = rule.split(';');
        let recurrence_freq = '';
        let recurrence_byday = [];
        let recurrence_until = '';

        parts.forEach(part => {
            const [key, value] = part.split('=');
            if (!key || !value) return;

            switch (key) {
                case 'FREQ':
                    recurrence_freq = value;
                    break;
                case 'BYDAY':
                    recurrence_byday = value.split(',').map(day => {
                        // Map uppercase BYDAY codes back to your short forms if needed
                        const reverseDayMap = { MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So' };
                        return reverseDayMap[day] || day;
                    });
                    break;
                case 'UNTIL':
                    // Convert from 20250617T000000Z to '2025-06-17' for date input
                    const dateStr = value.slice(0, 8); // "20250617"
                    recurrence_until = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
                    break;
                default:
                    break;
            }
        });

        return { recurrence_freq, recurrence_byday, recurrence_until };
    };

    const handleDelete = (index) => {
        const updated = localAppointments.filter((_, i) => i !== index);
        setLocalAppointments(updated);
        onChange(updated);
    };

    const handleAddException = (appointmentIndex) => {
        const updated = [...localAppointments];
        updated[appointmentIndex].exceptions.push({
            exception_date: '',
            is_cancelled: true,
            override_newdate: '',
            override_start: '',
            override_end: '',
            reason: '',
        });
        setLocalAppointments(updated);
        onChange(updated);
    };
    const formatDateForInput = (isoString) => {
        if (!isoString) return '';
        return isoString.slice(0, 10);
    };

    const handleDeleteException = (appointmentIndex, exceptionIndex) => {
        const updated = [...localAppointments];
        updated[appointmentIndex].exceptions.splice(exceptionIndex, 1);
        setLocalAppointments(updated);
        onChange(updated);
    };

    const handleExceptionChange = (appointmentIndex, exceptionIndex, field, value) => {
        const updated = [...localAppointments];
        updated[appointmentIndex].exceptions[exceptionIndex][field] = value;
        setLocalAppointments(updated);
        onChange(updated);
    };

    const weekDays = [
        { value: 'Mo', label: 'Montag' },
        { value: 'Di', label: 'Dienstag' },
        { value: 'Mi', label: 'Mittwoch' },
        { value: 'Do', label: 'Donnerstag' },
        { value: 'Fr', label: 'Freitag' },
        { value: 'Sa', label: 'Samstag' },
        { value: 'So', label: 'Sonntag' },
    ];
    return (
        <div className={styles.appointmentContainer}>
            {localAppointments.map((app, index) => (
                <fieldset key={index} className={styles.appointmentFieldset}>
                    <legend className={styles.appointmentLegend}>Termin {index + 1}</legend>
                    <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className={styles.removeButton}
                        title="Termin entfernen"
                    >
                        <Icons.Delete />
                    </button>

                    <div className={styles.row}>
                        <div>
                            <label className={styles.label}>Startzeit</label>
                            <input
                                className={styles.input}
                                type="time"
                                value={app.start_time}
                                onChange={e => handleAppointmentChange(index, 'start_time', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className={styles.label}>Endzeit</label>
                            <input
                                className={styles.input}
                                type="time"
                                value={app.end_time}
                                onChange={e => handleAppointmentChange(index, 'end_time', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className={styles.label}>Wiederholung</label>
                            <Select
                                options={[
                                    { value: '', label: 'Keine Wiederholung' },
                                    { value: 'DAILY', label: 'Täglich' },
                                    { value: 'WEEKLY', label: 'Wöchentlich' },
                                    { value: 'MONTHLY', label: 'Monatlich' },
                                    { value: 'YEARLY', label: 'Jährlich' },
                                ]}
                                value={{ value: app.recurrence_freq, label: app.recurrence_freq ? app.recurrence_freq.toLowerCase().charAt(0).toUpperCase() + app.recurrence_freq.toLowerCase().slice(1) : 'Keine Wiederholung' }}
                                onChange={selected => handleAppointmentChange(index, 'recurrence_freq', selected.value)}
                            />
                        </div>
                    </div>


                    <div className={styles.row}>
                        <div>
                            <label className={styles.label}>
                                {app.recurrence_freq ? 'Start (erster Termin)' : 'Datum'}
                            </label>
                            <input
                                className={styles.input}
                                type="date"
                                value={formatDateForInput(app.appointment_date)}
                                onChange={e => handleAppointmentChange(index, 'appointment_date', e.target.value)}
                            />
                        </div>
                        <div>
                            {app.recurrence_freq && (
                                <>

                                    <label className={styles.label}>Ende (letzter Termin)</label>
                                    <input
                                        type="date"
                                        className={styles.input}
                                        value={formatDateForInput(app.recurrence_until)}
                                        onChange={e => handleAppointmentChange(index, 'recurrence_until', e.target.value)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    {app.recurrence_freq === 'WEEKLY' && (
                        <div className={styles.row}>
                            <div>
                                <label className={styles.label}>Wochentag(e)</label>
                                <Select
                                    isMulti
                                    options={weekDays}
                                    value={app.recurrence_byday.map(day =>
                                        weekDays.find(d => d.value === day) || { value: day, label: day }
                                    )}
                                    onChange={selectedOptions => {
                                        const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                        handleAppointmentChange(index, 'recurrence_byday', selectedValues);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <div className={styles.row}>
                        <div>
                            <label className={styles.label}>Ort</label>
                            <Select
                                options={[{ value: null, label: 'Kein Ort' }, ...locations]}
                                value={locations.find(option => option.value === app.location_id) || null}
                                onChange={selected => {
                                    handleAppointmentChange(index, 'location_id', selected ? selected.value : null);
                                }}
                            />
                        </div>
                    </div>


                    <div>
                        <label className={styles.label}>Zusatzinfo</label>
                        <textarea
                            className={styles.textarea}
                            value={app.additional_text}
                            onChange={e => handleAppointmentChange(index, 'additional_text', e.target.value)}
                            placeholder='z.B. linke Spielfeldhälfte'
                        />
                    </div>
                    <div className={styles.exceptions}>
                        <h4>Ausnahmen</h4>
                        {app.exceptions.map((ex, exIdx) => (
                            <div key={exIdx} className={styles.exceptionItem}>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => handleDeleteException(index, exIdx)}
                                >
                                    <Icons.Delete />
                                </button>

                                <div className={styles.exceptionRow}>
                                    <div>
                                        <label className={styles.label}>Datum</label>
                                        <input
                                            className={styles.input}
                                            type="date"
                                            value={formatDateForInput(ex.exception_date)}
                                            onChange={e => handleExceptionChange(index, exIdx, 'exception_date', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className={styles.label}>Status</label>
                                        <select
                                            className={styles.input}
                                            value={ex.is_cancelled ? 'cancelled' : 'override'}
                                            onChange={e => handleExceptionChange(index, exIdx, 'is_cancelled', e.target.value === 'cancelled')}
                                        >
                                            <option value="cancelled">Ausgefallen</option>
                                            <option value="override">Geändert</option>
                                        </select>
                                    </div>
                                </div>

                                {!ex.is_cancelled && (
                                    <div className={styles.exceptionRow}>
                                        <div>
                                            <label className={styles.label}>Neues Datum</label>
                                            <input
                                                className={styles.input}
                                                type="date"
                                                value={formatDateForInput(ex.override_newdate)}
                                                onChange={e => handleExceptionChange(index, exIdx, 'override_newdate', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={styles.label}>Neue Startzeit</label>
                                            <input
                                                className={styles.input}
                                                type="time"
                                                value={ex.override_start}
                                                onChange={e => handleExceptionChange(index, exIdx, 'override_start', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className={styles.label}>Neue Endzeit</label>
                                            <input
                                                className={styles.input}
                                                type="time"
                                                value={ex.override_end}
                                                onChange={e => handleExceptionChange(index, exIdx, 'override_end', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className={styles.label}>Grund</label>
                                    <input
                                        className={styles.input}
                                        type="text"
                                        value={ex.reason}
                                        onChange={e => handleExceptionChange(index, exIdx, 'reason', e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}

                        <button type="button" className={styles.addExceptionButton} onClick={() => handleAddException(index)}>
                            <Icons.Add /> Ausnahme hinzufügen
                        </button>
                    </div>
                </fieldset>
            ))}
            <button type="button" onClick={handleAdd} className={styles.addButton}>
                <Icons.Add /> Termin hinzufügen
            </button>
        </div>
    );
}
